/**
 * Ocena stanu lokalu — 8 kategorii w skali 1–5, uśrednianych WAGOWO.
 *
 * Katalog kategorii żyje tutaj, a nie w komponencie React, bo tych samych etykiet
 * używają trzy różne konsumenty: kreator oferty (suwaki), raport wyceny (bilans)
 * i prompt opisu AI (uzasadnienie ceny). Rozjazd między nimi nie zapaliłby się
 * w typechecku — to zwykłe stringi przekraczające granicę modułu.
 *
 * Etykiety zostają po polsku: to teksty widoczne dla użytkownika, zgodnie z regułą
 * „kod po angielsku, treść dla człowieka po polsku".
 */

export type ConditionCategoryId =
  | "kitchen"
  | "bathroom"
  | "finish"
  | "windowsDoors"
  | "installations"
  | "outdoor"
  | "building"
  | "appeal";

/** Ocena pojedynczej kategorii — skala całkowita 1–5. */
export type ConditionRating = 1 | 2 | 3 | 4 | 5;

export interface ConditionCategory {
  id: ConditionCategoryId;
  /** Etykieta PL — nagłówek suwaka i wiersz w bilansie wyceny. */
  label: string;
  /** Opisy stopni skali, od oceny 1 do 5 (PL). Suwak pokazuje opis wybranej wartości. */
  scale: readonly [string, string, string, string, string];
  /**
   * Udział kategorii w ocenie łącznej. Suma wag = 1. Kuchnia i łazienka ważą
   * więcej niż widok z okna, bo to one decydują o cenie transakcyjnej — średnia
   * prosta zrównywała remont łazienki z „atrakcyjnością wizualną".
   */
  weight: number;
  /** Nazwa ikony w `Icon.tsx` — katalog trzyma ją, żeby lista suwaków była danymi, nie switchem w JSX. */
  icon: string;
}

export const CONDITION_CATEGORIES: readonly ConditionCategory[] = [
  {
    id: "kitchen",
    label: "Kuchnia",
    weight: 0.15,
    icon: "sofa",
    scale: ["Do remontu", "Zużyta", "Zadbana, standardowa, w pełni sprawna", "Dobra, nowsza zabudowa", "Nowa / po remoncie"],
  },
  {
    id: "bathroom",
    label: "Łazienka",
    weight: 0.15,
    icon: "wall",
    scale: ["Do remontu", "Zużyta", "Sprawna, czysta, standard z lat poprzednich", "Dobra, po odświeżeniu", "Nowa / po remoncie"],
  },
  {
    id: "finish",
    label: "Ściany i podłogi",
    weight: 0.15,
    icon: "brush",
    scale: ["Do remontu", "Zużyte", "Proste, równe, drobne ślady użytkowania", "Zadbane", "Nowe / po remoncie"],
  },
  {
    id: "windowsDoors",
    label: "Okna i drzwi",
    weight: 0.1,
    icon: "grid",
    scale: ["Do wymiany", "Zużyte", "Sprawne okna PCV, standardowe drzwi", "Dobre, szczelne", "Nowe"],
  },
  {
    id: "installations",
    label: "Instalacje (prąd, woda, ogrzewanie)",
    weight: 0.15,
    icon: "wand",
    scale: ["Do wymiany", "Wiekowe, awaryjne", "Sprawne, standard budynku", "Częściowo wymienione", "Nowe, po modernizacji"],
  },
  {
    id: "outdoor",
    label: "Balkon / taras / ogródek",
    weight: 0.1,
    icon: "home",
    scale: ["Brak", "Mały, zaniedbany", "Standardowy", "Dobry", "Duży, zadbany"],
  },
  {
    id: "building",
    label: "Budynek i klatka schodowa",
    weight: 0.1,
    icon: "building",
    scale: ["Do remontu", "Zaniedbane", "Standard", "Zadbane", "Nowe / po remoncie"],
  },
  {
    id: "appeal",
    label: "Atrakcyjność wizualna",
    weight: 0.1,
    icon: "eye",
    scale: ["Niska", "Poniżej przeciętnej", "Przeciętna", "Wysoka", "Bardzo wysoka / premium"],
  },
] as const;

export type ConditionRatings = Record<ConditionCategoryId, ConditionRating>;

/** Ocena neutralna — 3 w każdej kategorii daje wskaźnik stanu równy 0%. */
export const NEUTRAL_CONDITION_RATINGS: ConditionRatings = {
  kitchen: 3,
  bathroom: 3,
  finish: 3,
  windowsDoors: 3,
  installations: 3,
  outdoor: 3,
  building: 3,
  appeal: 3,
};

/** Punkt neutralny skali — ocena, przy której stan nie zmienia ceny. */
export const NEUTRAL_RATING = 3;

const CATEGORY_IDS = CONDITION_CATEGORIES.map((c) => c.id);

/**
 * Średnia WAŻONA ocen — wartość z przedziału [1, 5].
 *
 * Dzielimy przez sumę wag, a nie przez 1.0 zapisane na sztywno: gdyby ktoś dołożył
 * kategorię i zapomniał przeliczyć wagi, średnia po cichu wyszłaby poza skalę
 * zamiast się rozjechać w widoczny sposób.
 */
export function conditionScore(ratings: ConditionRatings): number {
  let weighted = 0;
  let totalWeight = 0;
  for (const category of CONDITION_CATEGORIES) {
    // Wagi mnożymy na liczbach CAŁKOWITYCH (punkty bazowe). Sumowanie 0.15 + 0.15 + …
    // w double daje 0.9999999999999999, przez co komplet ocen neutralnych wychodził
    // 2.9999999999999996 — czyli formalnie PONIŻEJ punktu neutralnego. Wycena wpadała
    // wtedy na gałąź „poniżej mediany" i pokazywała „−0,0%" przy zerowej korekcie.
    const basisPoints = Math.round(category.weight * 10_000);
    weighted += ratings[category.id] * basisPoints;
    totalWeight += basisPoints;
  }
  return totalWeight > 0 ? weighted / totalWeight : NEUTRAL_RATING;
}

/**
 * Opis słowny oceny — do podsumowania pod suwakami. Procentu tutaj NIE pokazujemy:
 * wpływ stanu na cenę zależy od rozrzutu cen w konkretnej próbie RCN, a ten komponent
 * próby nie zna. Liczbę podaje dopiero raport (`ValuationEstimate.conditionDelta`).
 */
export function conditionLabel(score: number): string {
  if (score < 1.75) return "Do gruntownego remontu";
  if (score < 2.5) return "Poniżej przeciętnej";
  if (score < 3.5) return "Przeciętny standard";
  if (score < 4.25) return "Powyżej przeciętnej";
  return "Stan premium";
}

/** Uzupełnia braki oceną neutralną — stare oferty nie mają zapisanych suwaków. */
export function normalizeRatings(partial: Partial<ConditionRatings> | null | undefined): ConditionRatings {
  const out = { ...NEUTRAL_CONDITION_RATINGS };
  if (!partial) return out;
  for (const id of CATEGORY_IDS) {
    const value = partial[id];
    if (value == null) continue;
    const rounded = Math.round(value);
    if (rounded >= 1 && rounded <= 5) out[id] = rounded as ConditionRating;
  }
  return out;
}
